import React, { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import useSequenceSelection from './useSequenceSelection';

export default function SequenceBuilder({ tokens, questionKey, disabled = false, correctTokenOrder, onComplete }) {
  const { selectedIds, select, undo, clear } = useSequenceSelection({ questionKey,
    capacity: tokens.length, tokenIds: tokens.map((token) => token.tokenId) });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const sent = useRef(false);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => { sent.current = false; setSubmitted(false); setError(''); }, [questionKey]);
  const locked = disabled || submitted || Boolean(correctTokenOrder);
  const byId = new Map(tokens.map((token) => [token.tokenId, token]));
  const displayed = correctTokenOrder || selectedIds;
  const submit = (ids) => {
    if (sent.current || disabled || ids.length !== tokens.length) return;
    sent.current = true;
    setSubmitted(true);
    setError('');
    Promise.resolve().then(() => onComplete([...ids])).catch((failure) => {
      if (!mounted.current) return;
      sent.current = false;
      setSubmitted(false);
      setError(failure?.message || 'Cevap kaydedilemedi.');
    });
  };
  const choose = (id) => {
    if (locked) return;
    const next = select(id);
    if (next?.length === tokens.length) submit(next);
  };
  return <Box sx={{ width: '100%', my: 1 }}>
    <Typography sx={{ textAlign: 'center', fontWeight: 800, mb: 1 }}>{correctTokenOrder ? 'Doğru sıralama' : 'Sayıları sırayla seç.'}</Typography>
    <Box role="group" aria-label="Sıralama kutucukları" sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 1 }}>
      {tokens.map((_, index) => <Box key={index} aria-label={`${index + 1}. sıra`} sx={{ minWidth: { xs: 52, md: 90 }, minHeight: { xs: 56, md: 80 }, px: 1,
        border: '2px solid #c4b5fd', borderRadius: 2, display: 'grid', placeItems: 'center', bgcolor: '#fff', fontWeight: 900, fontSize: { xs: 24, md: 34 } }}>
        {byId.get(displayed[index])?.displayText || '·'}
      </Box>)}
    </Box>
    <Box role="group" aria-label="Sayı kartları" sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 1, mt: 2 }}>
      {tokens.map((token) => <Button key={token.tokenId} aria-label={`${token.displayText} sayısını seç`} disabled={locked || selectedIds.includes(token.tokenId)} onClick={() => choose(token.tokenId)}
        sx={{ minWidth: { xs: 60, md: 100 }, minHeight: { xs: 56, md: 80 }, px: 1, border: '2px solid #a78bfa', borderRadius: 2, bgcolor: '#f5f3ff', color: '#251777',
          fontSize: { xs: 24, md: 34 }, fontWeight: 900, '&.Mui-disabled': { color: '#64748b', bgcolor: '#e2e8f0' } }}>
        {token.displayText}
      </Button>)}
    </Box>
    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 1 }}>
      <Button disabled={locked || !selectedIds.length} onClick={undo} sx={{ minHeight: 48 }}>Geri al</Button>
      <Button disabled={locked || !selectedIds.length} onClick={clear} sx={{ minHeight: 48 }}>Temizle</Button>
    </Box>
    <Box aria-live="polite" sx={{ textAlign: 'center', mt: 1 }}>
      {error || `${displayed.length} / ${tokens.length} sayı yerleştirildi.`}
      {error && <Button onClick={() => submit(selectedIds)}>Tekrar dene</Button>}
    </Box>
  </Box>;
}
